import React, { useEffect, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

export type CatalogPageType = 'cover' | 'inner-text' | 'image' | 'back-cover';

export interface CatalogPageData {
  id: string;
  type: CatalogPageType;
  imageUrl?: string;
  imageTitle?: string;
  imageSubtitle?: string;
  imageTextAlign?: 'left' | 'right';
  textSubtitle?: string;
  textTitle?: string;
  textContent?: string;
  isDark?: boolean;
}

const Page = React.forwardRef<HTMLDivElement, { children: React.ReactNode; number?: number }>((props, ref) => {
  return (
    <div className="bg-white w-full h-full relative overflow-hidden" ref={ref}>
      <div className="w-full h-full p-0 flex flex-col items-center justify-center">
        {props.children}
      </div>
      {props.number && (
        <div className="absolute bottom-4 right-4 text-xs font-bold text-gray-400">
          {props.number}
        </div>
      )}
    </div>
  );
});

export default function CatalogPage() {
  const [pages, setPages] = useState<CatalogPageData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const docRef = doc(db, 'pages', 'catalog');
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists() && snap.data().items) {
        setPages(snap.data().items);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const logoUrl = "https://static.readdy.ai/image/462a574349d19c517babfc55bf63e079/c8ac21bc970c8b7e4f4c5b80839b9d5d.png";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-xl font-bold text-slate-500 animate-pulse">Katalog Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')" }}>
      <Navbar />

      <div className="pt-28 pb-10 min-h-screen flex flex-col items-center justify-center">

        {/* 3D Flipbook Container */}
        <div className="flex-1 w-full flex items-center justify-center container mx-auto px-4 perspective-1000">
          <div className="shadow-2xl rounded-sm mx-auto" style={{ maxWidth: '1000px', width: '100%' }}>
            
            {pages.length > 0 ? (
              <React.Fragment>
                {/* @ts-ignore - react-pageflip specific typing workaround */}
                <HTMLFlipBook 
                  width={450} 
                  height={600} 
                  size="stretch"
                  minWidth={315}
                  maxWidth={500}
                  minHeight={420}
                  maxHeight={700}
                  maxShadowOpacity={0.3}
                  showCover={true}
                  mobileScrollSupport={true}
                  className="flip-book bg-white"
                >
                  {pages.map((p, index) => {
                    
                    // COVER PAGE RENDER
                    if (p.type === 'cover') {
                      return (
                        <Page key={p.id}>
                          <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 text-white flex flex-col items-center justify-center relative p-8">
                            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                            <div className="bg-white/95 px-6 py-4 rounded-3xl shadow-2xl mb-12 z-10">
                              <img src={logoUrl} alt="KARE YAPI" className="w-48 drop-shadow-sm" />
                            </div>
                            <div className="text-center z-10">
                              <h2 className="text-3xl font-light tracking-[0.2em] mb-2 uppercase">2026 Kurumsal</h2>
                              <h1 className="text-5xl font-bold text-red-500 mb-8" style={{ fontFamily: 'Raleway, sans-serif' }}>PROJE<br/>KATALOĞU</h1>
                              <div className="w-16 h-1 bg-white mx-auto"></div>
                            </div>
                            <div className="absolute bottom-8 left-0 right-0 text-center text-sm font-medium tracking-widest text-slate-400 z-10">
                              KARE YAPI &amp; İNŞAAT
                            </div>
                          </div>
                        </Page>
                      );
                    }

                    // BACK COVER PAGE RENDER
                    if (p.type === 'back-cover') {
                      return (
                        <Page key={p.id}>
                          <div className="w-full h-full bg-slate-900 text-white p-8 md:p-12 flex flex-col items-center justify-center border-l border-slate-700 relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/10 rounded-bl-full"></div>
                            <div className="bg-white/95 px-5 py-3 rounded-2xl shadow-2xl mb-10 z-10">
                              <img src={logoUrl} alt="KARE YAPI" className="w-32 drop-shadow-sm" />
                            </div>
                            <div className="text-center space-y-6 z-10">
                              <div>
                                <h4 className="text-red-500 font-bold text-[13px] tracking-widest mb-1 uppercase">Merkez Ofis</h4>
                                <p className="text-slate-300 text-sm leading-relaxed">Halkalı Merkez Mah. 1. Çeşme Sok. <br/> No: 13/E Küçükçekmece / İstanbul</p>
                              </div>
                              <div>
                                <h4 className="text-red-500 font-bold text-[13px] tracking-widest mb-1 uppercase">Bize Ulaşın</h4>
                                <p className="text-slate-300 text-sm leading-relaxed font-semibold">0 532 500 38 69 <br/> <a href="mailto:info@kareyapıinsaat.com" className="hover:text-white transition">info@kareyapıinsaat.com</a></p>
                              </div>
                            </div>
                            <div className="mt-16 text-slate-500 text-[11px] uppercase tracking-wider">
                              © 2026 KARE YAPI. Tüm hakları saklıdır.
                            </div>
                          </div>
                        </Page>
                      );
                    }

                    // IMAGE PAGE RENDER
                    if (p.type === 'image') {
                      return (
                        <Page key={p.id} number={index + 1}>
                          <div className="w-full h-full relative">
                            <img src={p.imageUrl || '/yapsat-site-7.png'} alt={p.imageTitle || 'Görsel'} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
                            
                            {/* Watermark Logo */}
                            <div className="absolute top-6 right-6 opacity-90 pointer-events-none select-none z-10">
                              <img src={logoUrl} alt="KARE YAPI" className="h-8 md:h-10 w-auto object-contain drop-shadow-md" />
                            </div>

                            <div className={`absolute bottom-10 ${p.imageTextAlign === 'left' ? 'left-8' : 'right-8 text-right'} text-white z-10 block pointer-events-none`}>
                              <h3 className="text-2xl font-bold font-['Raleway']">
                                {p.imageTitle?.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>)}
                              </h3>
                              {p.imageSubtitle && <p className="text-sm font-light tracking-wide opacity-90 drop-shadow-md">{p.imageSubtitle}</p>}
                            </div>
                          </div>
                        </Page>
                      );
                    }

                    // TEXT PAGE RENDER (inner-text)
                    return (
                      <Page key={p.id} number={index + 1}>
                        <div className={`w-full h-full ${p.isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-200'} p-8 md:p-12 flex flex-col justify-center border-r`}>
                          {(!p.isDark && index === 1) && <img src={logoUrl} alt="Logo" className="w-24 mb-10 opacity-80" />}
                          <h3 className="text-red-600 font-bold tracking-widest text-sm mb-3 uppercase">{p.textSubtitle}</h3>
                          <h2 className={`text-3xl lg:text-4xl font-bold ${p.isDark ? 'text-white' : 'text-slate-800'} mb-6 leading-tight`} style={{ fontFamily: 'Raleway, sans-serif' }}>
                            {p.textTitle?.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>)}
                          </h2>
                          
                          <div className={`${p.isDark ? 'text-slate-300' : 'text-slate-600'} leading-relaxed space-y-4`}>
                            {p.textContent?.split('\n').map((paragraph, i) => {
                              if (!paragraph.trim()) return null;
                              if (paragraph.startsWith('•') || paragraph.startsWith('-')) {
                                return (
                                  <ul key={i} className="mb-0 space-y-2 mt-4">
                                    <li className="flex items-center gap-3 text-sm">
                                      <i className="ri-check-line text-red-500 text-xl"></i> {paragraph.substring(1).trim()}
                                    </li>
                                  </ul>
                                );
                              }
                              return <p key={i}>{paragraph}</p>;
                            })}
                          </div>
                        </div>
                      </Page>
                    );
                  })}
                </HTMLFlipBook>
              </React.Fragment>
            ) : null}

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}